import { useEffect, useState } from 'react';
import { FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  useCreateRequestTemplate,
  useDeleteRequestTemplate,
  useRequestTemplates,
  useUpdateRequestTemplate,
} from '@/api/requestTemplates';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import type { RequestTemplate } from '@/types/api';

interface FormState {
  name: string;
  title: string;
  instructions: string;
  defaultDueInDays: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  title: '',
  instructions: '',
  defaultDueInDays: '',
};

function formatDueIn(days: number | null): string {
  if (days === null || days === undefined) return '—';
  return `${days} day${days === 1 ? '' : 's'}`;
}

export function RequestTemplatesPage() {
  usePageTitle('Request templates');
  const { data, isLoading, isError, error } = useRequestTemplates();
  const create = useCreateRequestTemplate();
  const update = useUpdateRequestTemplate();
  const del = useDeleteRequestTemplate();
  const toast = useToast();

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<RequestTemplate | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const [deleteTarget, setDeleteTarget] = useState<RequestTemplate | null>(null);

  useEffect(() => {
    if (!editOpen) return;
    if (editing) {
      setForm({
        name: editing.name,
        title: editing.title,
        instructions: editing.instructions ?? '',
        defaultDueInDays:
          editing.default_due_in_days !== null &&
          editing.default_due_in_days !== undefined
            ? String(editing.default_due_in_days)
            : '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [editOpen, editing]);

  const openNew = () => {
    setEditing(null);
    setEditOpen(true);
  };

  const openEdit = (tpl: RequestTemplate) => {
    setEditing(tpl);
    setEditOpen(true);
  };

  const parseDueInDays = (): number | null | 'invalid' => {
    const raw = form.defaultDueInDays.trim();
    if (raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > 3650) {
      return 'invalid';
    }
    return n;
  };

  const canSave =
    form.name.trim().length > 0 &&
    form.title.trim().length > 0 &&
    parseDueInDays() !== 'invalid';

  const handleSave = async () => {
    const dueIn = parseDueInDays();
    if (dueIn === 'invalid') {
      toast.error('Invalid due-in-days', 'Enter a whole number between 0 and 3650.');
      return;
    }
    const payload = {
      name: form.name.trim(),
      title: form.title.trim(),
      instructions: form.instructions.trim() ? form.instructions.trim() : null,
      default_due_in_days: dueIn,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, ...payload });
        toast.success('Template updated');
      } else {
        await create.mutateAsync(payload);
        toast.success('Template created');
      }
      setEditOpen(false);
      setEditing(null);
    } catch (e) {
      toast.error(editing ? 'Update failed' : 'Create failed', apiError(e));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await del.mutateAsync(deleteTarget.id);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error('Delete failed', apiError(e));
    }
  };

  const saving = create.isPending || update.isPending;

  return (
    <>
      <Card
        title="Request templates"
        actions={
          <Button
            variant="secondary"
            onClick={openNew}
            className="text-xs"
          >
            <Plus size={14} /> New template
          </Button>
        }
      >
        <p className="text-xs text-slate-500 mb-3">
          Save common file requests as templates so staff can pre-fill the form when creating a new request for a client.
        </p>
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : isError ? (
          <div className="text-sm text-red-600">{apiError(error)}</div>
        ) : (data?.length ?? 0) === 0 ? (
          <EmptyState
            title="No request templates"
            description="Create a template to instantiate the same file request across many clients in one click."
            icon={<FileText size={24} />}
            action={
              <Button variant="secondary" onClick={openNew}>
                <Plus size={14} /> New template
              </Button>
            }
          />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 px-3 font-semibold">Name</th>
                <th className="py-2 px-3 font-semibold">Title</th>
                <th className="py-2 px-3 font-semibold">Due in</th>
                <th className="py-2 px-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="py-2 px-3 font-medium text-slate-800">
                    {t.name}
                  </td>
                  <td className="py-2 px-3 text-slate-600">{t.title}</td>
                  <td className="py-2 px-3 text-slate-600 whitespace-nowrap">
                    {formatDueIn(t.default_due_in_days)}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="secondary"
                        className="text-xs"
                        onClick={() => openEdit(t)}
                      >
                        <Pencil size={12} /> Edit
                      </Button>
                      <Button
                        variant="secondary"
                        className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => setDeleteTarget(t)}
                      >
                        <Trash2 size={12} /> Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditing(null);
        }}
        title={editing ? 'Edit request template' : 'New request template'}
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setEditOpen(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!canSave}
              className="w-auto"
            >
              {editing ? 'Save changes' : 'Create'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="rt-name">Name</Label>
            <Input
              id="rt-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Q1 financials package"
            />
            <p className="text-xs text-slate-500 mt-1">
              Internal label staff pick from a dropdown.
            </p>
          </div>
          <div>
            <Label htmlFor="rt-title">Default request title</Label>
            <Input
              id="rt-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Q1 financial documents"
            />
          </div>
          <div>
            <Label htmlFor="rt-instructions">Instructions</Label>
            <textarea
              id="rt-instructions"
              rows={4}
              value={form.instructions}
              onChange={(e) =>
                setForm({ ...form, instructions: e.target.value })
              }
              placeholder="Details or guidance for the client"
              className="w-full border border-bb-lilac bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bb-navy/30 focus:border-bb-navy"
            />
          </div>
          <div>
            <Label htmlFor="rt-due">Default due in (days)</Label>
            <Input
              id="rt-due"
              type="number"
              min={0}
              max={3650}
              value={form.defaultDueInDays}
              onChange={(e) =>
                setForm({ ...form, defaultDueInDays: e.target.value })
              }
              placeholder="e.g. 14"
            />
            <p className="text-xs text-slate-500 mt-1">
              Optional. When set, instantiated requests default to today + N days.
            </p>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={
          deleteTarget ? `Delete "${deleteTarget.name}"?` : 'Delete template?'
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={del.isPending}
            >
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          This template will be removed. Existing file requests created from it
          are unaffected.
        </p>
      </Modal>
    </>
  );
}
